import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import {
  Tag,
  Info,
  Edit3,
  Calendar,
  FunnelX,
  User,
  FileUser,
  ReceiptText,
  CircleCheckBig,
  Layers,
  Layers2,
  UserRoundX,
  Eye
} from 'lucide-react';
import CopiarTag from '../shared/utils';
import apiClient from '../../api/client';
import dayjs from 'dayjs';




const API_URL = process.env.REACT_APP_API_URL;

const formatearFecha = (fecha) => {
  if (!fecha) return '';
  const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  const d = new Date(fecha);
  if (isNaN(d)) return '';
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = meses[d.getMonth()];
  const anio = d.getFullYear();
  return `${dia}-${mes}-${anio}`;
};

const normalizarTag = (valor) => String(valor || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

const convertirBooleano = (valor) => {
  const valorNormalizado = String(valor).toLowerCase();
  return valor === true || valorNormalizado === 'true' || valorNormalizado === '1';
};

const abrirTagEnPase = (tag) => {
  const tagNormalizado = normalizarTag(tag);
  if (!tagNormalizado) return;

  const numeroTag = tagNormalizado.replace(/^IMDM/, '');
  window.open(`https://apps.pase.com.mx/uc/detalletag/IMDM/${numeroTag}`, '_blank', 'noopener,noreferrer');
};

const TagPaseLink = ({ tag, children }) => (
  <span
    role="link"
    tabIndex={0}
    title="Abrir detalle del TAG en PASE"
    onClick={() => abrirTagEnPase(tag)}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        abrirTagEnPase(tag);
      }
    }}
    style={{ cursor: 'pointer', color: '#0056b3', textDecoration: 'underline' }}
  >
    {children || tag}
  </span>
);

const PaseStatusBadge = ({ tag, paseData }) => {
  const paseTag = (paseData || []).find((item) => normalizarTag(item.id) === normalizarTag(tag));
  const estado = !paseTag
    ? 'NO ENCONTRADO'
    : convertirBooleano(paseTag.activo) ? 'ACTIVO' : 'INACTIVO';
  const clase = estado === 'ACTIVO'
    ? 'bg-success text-dark'
    : estado === 'INACTIVO'
    ? 'bg-dark text-light'
    : 'bg-light text-dark';

  return (
    <span className={`badge ${clase} ml-1`} title={`Estado del TAG en PASE: ${estado}`}>
      {estado}
    </span>
  );
};

const PaseEconomicNumber = ({ tag, paseData }) => {
  const paseTag = (paseData || []).find((item) => normalizarTag(item.id) === normalizarTag(tag));
  return <span className="text-dark ml-1">{paseTag?.numeroEconomico || 'N/D'}</span>;
};

const TagAnalysisCell = ({ tag, paseData }) => (
  <div className="d-flex flex-column align-items-start mb-2">
    <div className="d-flex align-items-center flex-wrap">
      <TagPaseLink tag={tag}>{tag}</TagPaseLink>
      <PaseStatusBadge tag={tag} paseData={paseData} />
    </div>
    <small className="text-muted mt-1">
      Económico: <PaseEconomicNumber tag={tag} paseData={paseData} />
    </small>
  </div>
);

const estatusMap = {
  inactivo: {
    label: 'Inactivo (ausencia en la operación)',
    status: 'inactivo',
    color: 'text-warning',
    icon: UserRoundX
  },
  activo: {
    label: 'Activo en operación',
    status: 'activo',
    color: 'text-success',
    icon: CircleCheckBig
  },
  stockS: {
    label: 'Stock en Sahagún',
    status: 'stock',
    color: 'text-primary',
    icon: Layers
  },
  stockM: {
    label: 'Stock en Monterrey',
    status: 'stock',
    color: 'text-secondary',
    icon: Layers2

  },
  extravio: {
    label: 'TAG extraviado',
    status: 'extravio',
    color: 'text-danger',
    icon: ReceiptText
  }
};

const TagsTable = () => {
  const [showModal, setShowModal] = useState(false);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(250);
  const [vNoAclaracion, setvNoAclaracion] = useState('');
  const [vComentarios, setvComentarios] = useState('');
  const [isDictaminado, setIsDictaminado] = useState(false);
  const [tags, setTags] = useState([]);
  const [filtros, setFiltros] = useState({ fechaInicio: '', fechaFin: '', operador: '', tag: '', estatus: 'todos' });
  const [tagSeleccionado, setTagSeleccionado] = useState(null);
  const [fechaAclaracion, setFechaAclaracion] = useState(dayjs().format('YYYY-MM-DD'));
  const [loading, setLoading] = useState(true);
  const [paseData, setPaseData] = useState(null);
  const [discrepancias, setDiscrepancias] = useState([]);
  const [loadingComparison, setLoadingComparison] = useState(false);
  const [tagsAnalysis, setTagsAnalysis] = useState({ multipleTags: [], bajaTags: [] });


  useEffect(() => {
    (async () => {
      try {
        const [{ data: tagsData }, { data: analysisData }] = await Promise.all([
          apiClient.get(`/api/tags`),
          apiClient.get(`/api/tags/analysis/summary`)
        ]);
        setTags(tagsData);
        setTagsAnalysis(analysisData);
      } catch (err) {
        console.error('Error al cargar tags:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Función para cargar archivo JSON de PASE
  const handleLoadPaseJSON = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result);
        const content = jsonData.content || jsonData; // Maneja ambas estructuras
        
        if (!Array.isArray(content)) {
          alert('El JSON debe contener un array de objetos en "content" o ser directamente un array.');
          return;
        }

        setPaseData(content);
        compararDatos(content);
      } catch (error) {
        console.error('Error al parsear JSON:', error);
        alert('Error al cargar el archivo JSON. Verifica que sea válido.');
      }
    };
    reader.readAsText(file);
  };

  // Función para comparar datos entre PASE y TUSA
  const compararDatos = (paseContent) => {
    setLoadingComparison(true);
    const discrepanciasList = [];

    paseContent.forEach((paseTag) => {
      const paseTagId = normalizarTag(paseTag.id);
      
      // Buscar el tag correspondiente en TUSA
      const tusaTag = tags.find(t => normalizarTag(t.Dispositivo) === paseTagId);

      if (tusaTag) {
        // Comparar estados activo/inactivo
        const paseActivo = convertirBooleano(paseTag.activo);
        const tusaExtraviado = tusaTag.Estatus_Secundario === 'extravio' || Boolean(tusaTag.Fecha_Extravio);
        const tusaActivo = !tusaExtraviado && (convertirBooleano(tusaTag.Activa) || tusaTag.Estatus_Secundario === 'activo');

        if (paseActivo !== tusaActivo) {
          discrepanciasList.push({
            id: paseTagId,
            prefijo: paseTag.prefijo,
            numero: paseTag.numero,
            statusPase: paseActivo ? 'ACTIVO' : 'INACTIVO',
            statusTusa: tusaActivo ? 'Activo' : tusaTag.Estatus_Secundario,
            clase: paseTag.nombreClase,
            tipo: paseTag.tipoFisico,
            observaciones: tusaTag.Observaciones || '-',
            saldo: paseTag.saldo,
            domiciliado: paseTag.domiciliado,
            regionalizado: paseTag.regionalizado
          });
        }
      } else {
        // TAG encontrado en PASE pero no en TUSA
        discrepanciasList.push({
          id: paseTagId,
          prefijo: paseTag.prefijo,
          numero: paseTag.numero,
          statusPase: convertirBooleano(paseTag.activo) ? 'ACTIVO' : 'INACTIVO',
          statusTusa: 'NO ENCONTRADO EN TUSA',
          clase: paseTag.nombreClase,
          tipo: paseTag.tipoFisico,
          observaciones: 'Tag no existe en el sistema TUSA',
          saldo: paseTag.saldo,
          domiciliado: paseTag.domiciliado,
          regionalizado: paseTag.regionalizado
        });
      }
    });

    setDiscrepancias(discrepanciasList);
    setShowComparisonModal(true);
    setLoadingComparison(false);
    
    // Log para debugging
  };

  // Función para generar CSV de discrepancias
  const generarCSVDiscrepancias = () => {
    if (!discrepancias || discrepancias.length === 0) return '';

    const headers = [
      'TAG (ID)',
      'Número',
      'Estado PASE',
      'Estado TUSA',
      'Clase',
      'Tipo Físico',
      'Saldo',
      'Domiciliado',
      'Observaciones'
    ];

    const rows = discrepancias.map((disc) => [
      disc.id,
      disc.numero,
      disc.statusPase,
      disc.statusTusa,
      disc.clase,
      disc.tipo,
      disc.saldo.toFixed(2),
      disc.domiciliado ? 'Sí' : 'No',
      `"${disc.observaciones.replace(/"/g, '""')}"` // Escapar comillas
    ]);

    const csv = [
      headers.join(','),
      ...rows.map((row) => row.join(','))
    ].join('\n');

    return csv;
  };

  // Función para descargar CSV
  const descargarCSV = (csv) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `discrepancias_tags_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.csv`;
    link.click();
  };

  // Función para generar responsiva
  const generarResponsiva = async (tag) => {
    const data = {
      nombre: tag?.Nombres + " " + tag?.Ap_paterno + " " + tag?.Ap_materno,
      matricula: (tag?.id_matricula),
      numeroDispositivo: tag?.Dispositivo,
      fechaAsignacion: tag?.Fecha_alta ? tag.Fecha_alta.split('T')[0] : ''
    };

    const response = await apiClient.post(`/api/tags/exportar-responsiva`, data, {
      responseType: 'blob'
    });

    const url = window.URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Responsiva_${data.numeroDispositivo}.xlsx`;
    a.click();
  };

  const handleChange = ({ target: { name, value, checked, type } }) => {
    if (name === 'inAclaracionN') return setvNoAclaracion(value);
    if (name === 'inDictaminado') return setIsDictaminado(checked);
    if (name === 'inFechaCreacion') return setFechaAclaracion(value);
    if (name === 'inComentarios') return setvComentarios(value);
  };

  const getEstatusInfo = (key) => estatusMap[key] || estatusMap.inactivo;

  const abrirModal = (tag) => {
    tag.Tag = tag.Dispositivo;
    setTagSeleccionado(tag);
    setvNoAclaracion(tag.NoAclaracion || '');
    setvComentarios(tag.Observaciones || '');
    setIsDictaminado(!!tag.Aplicado);
    setShowModal(true);
  };

  const cerrarModal = () => {
    setShowModal(false);
    setTagSeleccionado(null);
  };

  const cerrarModalGuardando = async () => {
    if (!vNoAclaracion) {
      return alert('Introduce el Número de aclaración para guardar.');
    }
    try {
      const id = tagSeleccionado.ID;
      const nuevoEstatus = isDictaminado ? 'dictaminado' : 'aclaracion_levantada';
      const payload = { noAclaracion: vNoAclaracion, FechaDictamen: fechaAclaracion, estatusSecundario: nuevoEstatus, observaciones: vComentarios, dictaminado: isDictaminado ? 1 : 0 };
      const { status, data } = await apiClient.patch(`/api/aclaraciones/${id}/UpdateAclaracion`, payload);
      if (status === 200) {
        setTags(prev => prev.map(c => c.ID === id ? { ...c, Estatus_Secundario: nuevoEstatus, FechaDictamen: fechaAclaracion, observaciones: vComentarios, NoAclaracion: vNoAclaracion, Aplicado: payload.dictaminado } : c));
      } else console.error('No se pudo actualizar el estatus');
    } catch (error) {
      console.error('Error al cambiar el estatus:', error);
    }
    cerrarModal();
  };

  const tagsFiltrados = tags.filter(t => {
    const fechaOK = (!filtros.fechaInicio || t.Fecha >= filtros.fechaInicio) && (!filtros.fechaFin || t.Fecha <= filtros.fechaFin);
    const opOK = !filtros.operador || t.No_Economico.toLowerCase().includes(filtros.operador.toLowerCase());
    const tagOK = !filtros.tag || t.Dispositivo.toLowerCase().includes(filtros.tag.toLowerCase());
    const estOK = filtros.estatus === 'todos' || t.Estatus_Secundario === filtros.estatus;
    return fechaOK && opOK && tagOK && estOK;
  });

  const multipleTagsVisibles = paseData
    ? tagsAnalysis.multipleTags
      .map((item) => {
        const tagsEncontrados = String(item.tags || '')
          .split(', ')
          .filter((tag) => tag && paseData.some((paseTag) => normalizarTag(paseTag.id) === normalizarTag(tag)));

        return {
          ...item,
          totalTags: tagsEncontrados.length,
          tags: tagsEncontrados.join(', ')
        };
      })
      .filter((item) => item.totalTags > 1)
    : tagsAnalysis.multipleTags;

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "15vh" }}>
        <div className="spinner-border text-primary" role="status" style={{ width: "4rem", height: "4rem" }}>
          <span className="visually-hidden">.</span>
        </div>
      </div>
    );
  }

  return (
    <div>

      {/* Card contenedor */}
      <div className="wrapper">

        <div className="card shadow">
          <div className="card-header py-3 d-flex flex-column flex-md-row align-items-md-center justify-content-between">
            <div className="d-flex align-items-center gap-2">
              <label htmlFor="rowsSelect" className="input-group-text mb-0 mr-2">Registros por página:</label>
              <select
                id="rowsSelect"
                className="form-select form-select-sm custom-select"
                value={rowsPerPage}
                onChange={(e) => setRowsPerPage(parseInt(e.target.value))}
                style={{ width: 'auto' }}
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={250}>250</option>
              </select>
            </div>

            <h6 className="m-0 font-weight-bold text-primary ml-3" style={{ flex: 'auto', }}>Tags que aplican según los parametros seleccionados  - (<span className="text sm">{tagsFiltrados.length})</span></h6>

          </div>

          {/* Filtros */}
          <div className="card-body pb-0">
            <div className="d-flex flex-wrap gap-2 mb-3">

              <div className="form-floating pr-2">
                <input
                  type="date"
                  value={filtros.fechaInicio}
                  onChange={(e) => setFiltros({ ...filtros, fechaInicio: e.target.value })}
                  id='inFechaInicio'
                  className="form-control form-control-sm"
                />
                <label className="form-label" htmlFor='inFechaInicio'>Fecha Inicio</label>

              </div>

              <div className="form-floating pr-2">
                <input
                  type="date"
                  value={filtros.fechaFin}
                  id="inFechaFin"
                  onChange={(e) => setFiltros({ ...filtros, fechaFin: e.target.value })}
                  className="form-control form-control-sm"
                />
                <label className='' htmlFor="inFechaFin" >Fecha Fin</label>

              </div>


              <div className="form-floating pr-2">
                <input className="form-control form-control-sm" type="text"
                  placeholder="Buscar operador..."
                  value={filtros.operador}
                  id="MatriculaOP"
                  autoComplete="off"
                  onChange={(e) => setFiltros({ ...filtros, operador: e.target.value })} />
                <label htmlFor="MatriculaOP">Matricula / Operador</label>
              </div>



              <div className="form-floating pr-2">

                <input
                  type="text"
                  placeholder="Buscar tag..."
                  value={filtros.tag}
                  id="TagAclaracion"
                  autoComplete="off"
                  onChange={(e) => setFiltros({ ...filtros, tag: e.target.value })}
                  className="form-control form-control-sm"
                />
                <label className="form-label" htmlFor='TagAclaracion'>Tag</label>

              </div>

              <div className="col-md-2  pr-2 input-group-text mb-0 ">
                <label className='form-label' htmlFor='inEstatus'>Estatus:</label>
                <select className='mx-3 p-2' id='inEstatus' name="Estatus" onChange={(e) => setFiltros({ ...filtros, estatus: e.target.value })} value={filtros.estatus}>
                  <option value="todos">Todos</option>
                  <option value="activo">Activos</option>
                  <option value="inactivo">Inactivos</option>
                  <option value="extravio">Extraviados</option>
                  <option value="stockM">Stock Monterrey</option>
                  <option value="stockS">Stock Sahagún</option>
                </select>
              </div>

              {/* Botón para resetear los filtros. */}
              <div className="ml-3 pr-1 pt-1">
                <button
                  className="btn btn-sm btn btn-outline-dark rounded-3"
                  onClick={() => setFiltros({
                    fechaInicio: '',
                    fechaFin: '',
                    operador: '',
                    tag: '',
                    estatus: 'todos'
                  })}>
                  <FunnelX size={40} className="me-1" />
                </button>
              </div>

              {/* Botón para cargar JSON de PASE */}
              <div className="ml-2 pr-1 pt-1">
                <label htmlFor="paseJsonInput" className="btn btn-sm btn-outline-info rounded-3 m-0" style={{ cursor: 'pointer' }}>
                  📄 Cargar JSON PASE
                </label>
                <input
                  id="paseJsonInput"
                  type="file"
                  accept=".json"
                  onChange={handleLoadPaseJSON}
                  style={{ display: 'none' }}
                />
              </div>
            </div>

            {/* Tabla */}
            <div className="table-responsive table-container">
              <table className="table table-sm table-hover table-scroll align-middle">
                <thead className="table-light">
                  <tr>
                    <th className='col-1'>Fecha modificación</th>
                    <th className='col-1'>Colaborador</th>
                    <th className='col-1'>TAG</th>
                    <th className='col-1'>Situación personal</th>
                    <th className='col-1'>Estatus</th>
                    <th className='col-2'>Observaciones</th>
                    <th className='col-1'> </th>
                  </tr>
                </thead>
                <tbody>
                  {/* Se crea una fila de la tabla con cada registro obtenido de tagsFiltrados */}
                  {tagsFiltrados.map((tag, i) => {
                    const estatusInfo = getEstatusInfo(tag.Estatus_Secundario);
                    const IconoEstatus = estatusInfo?.icon;
                    const Fecha = ((estatusInfo?.status === 'extravio') ? tag.Fecha_Extravio : (estatusInfo?.status === 'stock' || estatusInfo?.status === 'activo') ? tag.Fecha_alta : (estatusInfo?.status === 'inactiva') ? tag.Fecha_inactiva : tag.Fecha);
                    return (
                      <tr key={i}>
                        <td style={{ alignContent: 'center' }}>
                          <div className="d-flex align-items-center" style={{ alignContent: 'center' }}>
                            <Calendar className="me-2 text-secondary mr-2" size={30} />
                            <div>
                              <div className="fw-bold">{Fecha?.split('T')[0]}</div>
                              <small className="text-muted">{tag?.Fecha?.split('T')[1]?.split('.')[0]}</small>
                            </div>
                          </div>
                        </td>
                        <td style={{ alignContent: 'center' }}>
                          <div className="d-flex align-items-center">
                            <User className="me-2 text-secondary" size={30} />
                            <div className='ml-2' style={{ display: 'inline' }}>
                              <div className="fw-bold inline">{tag.id_matricula} </div><div className='inline'>{tag.Nombres}</div>
                              <small className="text-muted">{(tag.Ap_paterno || '') + " " + (tag.Ap_materno || '')} </small>
                            </div>
                          </div>
                        </td>
                        <td style={{ alignContent: 'center' }}>
                          <div className="d-flex align-items-center">
                            <a href={`https://apps.pase.com.mx/uc/detalletag/IMDM/${tag?.Dispositivo?.replace("IMDM", "")}`} style={{ fontSize: '.9rem', }} target='_blank' rel="noreferrer"  >
                              <Tag className="me-2 text-secondary" size={30} />
                            </a>

                            <big className="text-secondary ml-2">
                              <TagPaseLink tag={tag.Dispositivo}>{tag.Dispositivo}</TagPaseLink>
                            </big>
                          </div>
                        </td>

                        <td style={{ alignContent: 'center' }}>
                          <strong>{tag.Estado_Personal}</strong>
                        </td>


                        <td style={{ alignContent: 'center' }}>
                          <span className={`d-inline-flex  align-items-center ${estatusInfo?.color}`}>
                            <IconoEstatus size={16} className="me-1 mr-2" />
                            {estatusInfo?.label}
                          </span>
                        </td>


                        <td style={{ alignContent: 'center' }} className='col-2'>
                          <textarea id={`observación-${i}`} className={`d-inline-flex form-control  align-items-center ${estatusInfo?.color}`}
                            style={{ alignContent: 'center', width: '100%', height: '4rem', resize: 'none', border: 'none', background: (estatusInfo.status === 'inactivo') ? 'navy' : 'gainsboro', fontSize: '0.75rem', fontWeight: 'lighter', overflow: 'auto' }}
                            readOnly value={tag.Observaciones || 'Sin observaciones'} />
                        </td>

                        <td style={{ alignContent: 'center' }}>
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-sm btn-outline-success mr-2"
                              title="Gestionar aclaración"
                              onClick={() => abrirModal(tag)}
                            >
                              <Edit3 size={14} />
                            </button>
                            <button
                              className="btn btn-sm btn-outline-success ml-1"
                              title="Generar responsiva"
                              onClick={() => generarResponsiva(tag)}
                            >
                              <Eye size={14} className='warning' />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="md">
        <Modal.Header style={{ paddingBottom: '0.5rem' }}>
          <Modal.Title>
            <span className='' style={{ float: 'right !important', right: '10px', position: 'absolute', fontSize: '.7rem' }}>ID: {tagSeleccionado?.ID}</span>
            <FileUser size={35} className="me-1" ></FileUser> Gestión del TAG
            <br></br>

            <a href={`https://apps.pase.com.mx/uc/detalletag/IMDM/${tagSeleccionado?.Dispositivo?.replace("IMDM", "")}`} style={{ fontSize: '.9rem', }} target='_blank' rel="noreferrer"  >

              <ReceiptText size={23} className="me-1 ml-2 mr-3" ></ReceiptText >
              TAG: {tagSeleccionado?.Dispositivo}
            </a>

            {/*Este es el componente al que */}
            <CopiarTag cruceSeleccionado={tagSeleccionado} />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ paddingTop: '0.5rem' }}>
          <div className="card" style={{ background: 'whitesmoke', }}>
            <div className='card-header text-center'>
              <Info size={15} className="me-1" />
              <span className='text-primary font-weight-bolder ml-2'>Información del tag</span>

            </div>
            <div className="card-body" style={{ background: 'whitesmoke', }}>

              <div className='form-row align-items-center'>

                <div className="form-floating pr-2 col">

                  Fecha:
                  <span className='ml-1 text-secondary font-weight-bolder'>
                    <span style={{ cursor: 'pointer', fontSize: '0.85rem' }}
                      title="Copiar FECHA al portapapeles"
                      onClick={() => {
                        if (tagSeleccionado?.Fecha_alta) {
                          navigator.clipboard.writeText(formatearFecha(tagSeleccionado?.Fecha_alta));
                        }
                      }}
                    >
                      {formatearFecha(tagSeleccionado?.Fecha_alta)}
                    </span>
                  </span>

                  <br></br>


                  Matricula/Operador:
                  <span className='ml-1 text-secondary font-weight-bolder'> {tagSeleccionado?.["No_Economico"]}</span>

                  <br></br>


                  Tag:
                  <span className='ml-1 text-secondary font-weight-bolder' style={{ cursor: 'pointer', fontSize: '0.85rem' }} title="Copiar CASETA al portapapeles"
                    onClick={() => {
                      if (tagSeleccionado?.Tag) {
                        navigator.clipboard.writeText(tagSeleccionado?.Tag);
                      }
                    }}> 
                    <TagPaseLink tag={tagSeleccionado?.Tag}>{tagSeleccionado?.Tag}</TagPaseLink>
                  </span>

                  <br></br>



                  Cruces del úlltimo mes (#):
                  <span className='ml-1 text-secondary font-weight-bolder'> {tagSeleccionado?.Importe?.toFixed(2)}</span>

                  <br></br>



                  Monto acumulado de los cruces del último mes:
                  <span className='ml-1 text-secondary font-weight-bolder'>$ {tagSeleccionado?.diferencia?.toFixed(2)}</span>

                </div>


              </div>

              <div className='form-row align-items-center'>
                                <div className="col">
                  <div className="form-group mb-4"><label htmlFor='inObservaciones'>Observaciones</label>
                    <textarea name="inComentarios" className="form-control" rows="3" id="inObservaciones" placeholder="Comentarios..." value={vComentarios} onChange={handleChange} />

                  </div>
                </div>


                <div className="pl-4 col">
                  <div className="form-check form-switch row " style={{ textAlign: 'center', height: '2rem' }}>
                    <input className="form-check-input" type="checkbox" id="CheckDictaminado" name='inDictaminado' onChange={handleChange} checked={isDictaminado} />
                    <label className="form-check-label" htmlFor="CheckDictaminado">Activo</label>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => cerrarModalGuardando(tagSeleccionado)}>
            Guardar cambios
          </Button>
          <Button variant="secondary" onClick={() => cerrarModal(tagSeleccionado)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Comparación de Datos PASE vs TUSA */}
      <Modal show={showComparisonModal} onHide={() => setShowComparisonModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            🔍 Comparación de TAGs: PASE vs TUSA
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto', color: '#212529' }}>
          {loadingComparison ? (
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Comparando datos...</span>
              </div>
              <p className="text-muted mt-2">Procesando archivo...</p>
            </div>
          ) : (
            <>
              {/* Resumen General */}
              <div className="alert alert-info mb-3" style={{ color: '#123047' }}>
                <h6 className="alert-heading font-weight-bold">📊 Resumen de la Comparación</h6>
                <div className="row">
                  <div className="col-md-4">
                    <strong>TAGs de PASE:</strong> <span className="text-primary">{paseData?.length || 0}</span>
                  </div>
                  <div className="col-md-4">
                    <strong>Con Discrepancias:</strong> <span className="text-danger">{discrepancias.length}</span>
                  </div>
                  <div className="col-md-4">
                    <strong>Sincronizados:</strong> <span className="text-success">{(paseData?.length || 0) - discrepancias.length}</span>
                  </div>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6 mb-3 mb-md-0">
                  <div className="card h-100 border-warning">
                    <div className="card-header bg-warning text-dark d-flex justify-content-between align-items-center">
                      <strong>Matrículas con más de un TAG</strong>
                      <span className="badge bg-light">{multipleTagsVisibles.length}</span>
                    </div>
                    <div className="card-body p-2">
                      {multipleTagsVisibles.length === 0 ? (
                        <p className="text-muted mb-0">No se encontraron matrículas con múltiples TAGs.</p>
                      ) : (
                        <div className="table-responsive table-container">
                          <table className="table table-sm table-bordered mb-0" style={{ color: '#212529', tableLayout: 'fixed' }}>
                            <thead>
                              <tr><th style={{ width: '18%' }}>Matrícula</th><th style={{ width: '30%' }}>Operador</th><th>TAGs / Económico</th></tr>
                            </thead>
                            <tbody>
                              {multipleTagsVisibles.map((item) => (
                                <tr key={item.matricula}>
                                  <td>{item.matricula}</td>
                                  <td>{item.operador}</td>
                                  <td>
                                    <strong className="d-block mb-2">{item.totalTags} TAGs activos</strong>
                                    <div className="text-muted" style={{ fontSize: '0.875em' }}>
                                      {String(item.tags || '').split(', ').map((tag, tagIndex) => (
                                        <TagAnalysisCell key={`${tag}-${tagIndex}`} tag={tag} paseData={paseData} />
                                      ))}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="card h-100 border-danger">
                    <div className="card-header bg-danger text-white d-flex justify-content-between align-items-center">
                      <strong>TAGs de operadores dados de baja</strong>
                      <span className="badge bg-light text-danger">{tagsAnalysis.bajaTags.length}</span>
                    </div>
                    <div className="card-body p-2">
                      {tagsAnalysis.bajaTags.length === 0 ? (
                        <p className="text-muted mb-0">No se encontraron TAGs asociados a bajas o renuncias.</p>
                      ) : (
                        <div className="table-responsive table-container">
                          <table className="table table-sm table-bordered mb-0" style={{ color: '#212529', tableLayout: 'fixed' }}>
                            <thead>
                              <tr><th style={{ width: '25%' }}>Matrícula</th><th>TAG / Económico</th><th style={{ width: '26%' }}>Motivo</th></tr>
                            </thead>
                            <tbody>
                              {tagsAnalysis.bajaTags.map((item) => (
                                <tr key={`${item.matricula}-${item.tag}`}>
                                  <td>{item.matricula}<br /><small className="text-muted">{item.operador}</small></td>
                                  <td>
                                    <TagAnalysisCell tag={item.tag} paseData={paseData} />
                                  </td>
                                  <td>{item.motivo}<br /><small className="text-muted">{item.fechaBaja || 'Sin fecha'}</small></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Mensaje si NO hay discrepancias */}
              {discrepancias.length === 0 ? (
                <div className="alert alert-success" role="alert" style={{ color: '#155724' }}>
                  <h5 className="alert-heading font-weight-bold">✅ ¡Perfectamente Sincronizado!</h5>
                  <p>
                    Todos los <strong>{paseData?.length || 0} TAGs</strong> del archivo PASE coinciden 
                    con tu sistema TUSA. No se encontraron discrepancias.
                  </p>
                  <hr />
                  <p className="mb-0">
                    <small className="text-muted">
                      Última comparación: {dayjs().format('DD/MM/YYYY HH:mm:ss')}
                    </small>
                  </p>
                </div>
              ) : (
                <>
                  {/* Mensaje si hay discrepancias */}
                  <div className="alert alert-warning mb-3" style={{ color: '#664d03' }}>
                    <h6 className="alert-heading font-weight-bold">⚠️ Se encontraron discrepancias</h6>
                    <p className="mb-0">
                      Se detectaron <strong>{discrepancias.length}</strong> TAGs con diferencias entre PASE y TUSA. 
                      Revisa la tabla a continuación para más detalles.
                    </p>
                  </div>

                  {/* Tabla de discrepancias */}
                  <div className="table-responsive table-container">
                    <table className="table table-sm table-hover table-bordered" style={{ color: '#212529' }}>
                      <thead className="table-danger">
                        <tr>
                          <th>TAG (ID)</th>
                          <th>Estado PASE</th>
                          <th>Estado TUSA</th>
                          <th>Clase</th>
                          <th>Tipo Físico</th>
                          <th>Saldo</th>
                          <th>Domiciliado</th>
                          <th>Observaciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {discrepancias.map((disc, idx) => (
                          <tr
                            key={idx}
                            className={
                              disc.statusTusa === 'NO ENCONTRADO EN TUSA'
                                ? 'table-warning'
                                : 'table-info'
                            }
                          >
                            <td>
                              <strong><TagPaseLink tag={disc.id}>{disc.id}</TagPaseLink></strong>
                              <br />
                              <small className="text-muted">{disc.numero}</small>
                            </td>
                            <td>
                              <span
                                className={
                                  disc.statusPase === 'ACTIVO'
                                    ? 'badge bg-success'
                                    : 'badge bg-secondary'
                                }
                              >
                                {disc.statusPase}
                              </span>
                            </td>
                            <td>
                              <span
                                className={
                                  disc.statusTusa === 'Activo'
                                    ? 'badge bg-success'
                                    : disc.statusTusa === 'NO ENCONTRADO EN TUSA'
                                    ? 'badge bg-danger'
                                    : 'badge bg-warning'
                                }
                              >
                                {disc.statusTusa}
                              </span>
                            </td>
                            <td>{disc.clase}</td>
                            <td>{disc.tipo}</td>
                            <td className="text-end">${disc.saldo.toFixed(2)}</td>
                            <td className="text-center">
                              {disc.domiciliado ? '✓' : '-'}
                            </td>
                            <td style={{ fontSize: '0.85rem', maxWidth: '200px', color: '#212529' }}>
                              {disc.observaciones}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowComparisonModal(false)}>
            Cerrar
          </Button>
          {discrepancias.length > 0 && (
            <Button
              variant="warning"
              onClick={() => {
                const csv = generarCSVDiscrepancias();
                descargarCSV(csv);
              }}
            >
              📥 Descargar CSV
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>


  );
};

export default TagsTable;
